import React, { useCallback, useContext, useReducer } from "react";
import { Commands } from 'shared/resources/BitbiteCommands';
import { SessionContext } from 'shared/system-controls/session/SessionProvider';
import OrderReducer from './OrderReducer';
import UtilityAPI from "shared/utils/UtilityAPI";

export const OrderContext = React.createContext();

//----------------------------
const OrderState = ({ children }) => {
    const initialState = {
    };
    const [state, dispatch] = useReducer(OrderReducer, initialState);
    const { asyncServiceCall } = useContext(SessionContext);

    //--------------------------------------------------------
    const getOrdersCount = useCallback(async () => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_LIST_COUNT_GET);
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            return res;
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const getOrders = useCallback(async ({ userID, states, fromDate, toDate, updateStateOnNewTab, first, count }) => {
        try {
            dispatch({ type: 'LOADING' });
            let res = await asyncServiceCall(
                userID ? Commands.API_URL_BITBITE_BO_USER_ORDER_LIST_GET(userID)
                    : Commands.API_URL_BITBITE_BO_ORDER_LIST_GET, { userID, states, fromDate, toDate, first, count });
            dispatch({ type: 'LOADED' });
            updateStateOnNewTab && dispatch({ type: 'UPDATE_ORDER_NEW_TAB', payload: { ...res, first, count } });
            !updateStateOnNewTab && dispatch({ type: 'SET_ORDERS', payload: { ...res, first, count } });
            return res;

        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const getNextOrder = useCallback(async () => {
        try {
            dispatch({ type: 'LOADING' });
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_NEXT_GET);
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            dispatch({ type: 'LOADED' });
            if (!res.order) {
                dispatch({ type: 'SET_STATE_DATA', payload: { order: undefined, message: Object.assign({}, { text: 'NO_MORE_ORDER', type: 'warning' }) } });
                setTimeout(() => getNextOrder(), 60000);
            }
            else {
                dispatch({ type: 'SET_STATE_DATA', payload: { orderListVisible: undefined } });
            }
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const confirmOrder = useCallback(async ({ order, note, callback }) => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_CONFIRM(order?._id), { note });
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const orderRequiresCooking = useCallback(async ({ order, requiresCookingSectionIDs, callback }) => {
        try {
            if (!requiresCookingSectionIDs?.length) {
                console.log('requiresCookingSectionIDs is empty', requiresCookingSectionIDs);
                return;

            }
            await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_REQUIRES_COOKING(order?._id), { requiresCookingSectionIDs });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const orderReadyToPack = useCallback(async ({ order, callback }) => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_PORTION(order?._id));
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const orderPacked = useCallback(async ({ order, callback }) => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_PORTION(order?._id));
            res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_PACKED(order?._id));
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const orderDelivered = useCallback(async ({ order, callback }) => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_DELIVERED(order?._id));
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const orderIsNotReady = useCallback(async ({ order, callback }) => {
        try {
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_IS_NOT_READY(order?._id));
            dispatch({ type: 'SET_STATE_DATA', payload: res });
            callback && callback();
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const prepareOrder = useCallback(async ({ orderID, order }) => {

        try {
            if (orderID) {
                let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_GET(orderID));
                order = res.order;
                dispatch({ type: 'SET_STATE_DATA', payload: { originalOrder: order } });
            }
            if (!order) {
                return;
            }
            order = { ...order };
            delete order.users;
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_PREPARE, { order });
            dispatch({ type: 'SET_STATE_DATA', payload: { ...res, updateOrderData: undefined } });
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const createNewAccount = useCallback(async ({ data, callback }) => {

        try {
            const { fullname, mobile, detail, plaque, unit } = data || {};
            if ([fullname, mobile, detail, plaque, unit].some(c => !c)) {
                return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
            }
            dispatch({ type: 'LOADING' });

            const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_CUSTOMER_ACCOUNT_CREATE, data);
            dispatch({ type: 'LOADED' });
            callback && callback(res.userID);
        }
        catch (err) {
            console.log(err);
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const saveNewAddress = useCallback(async ({ user, address, callback }) => {

        try {
            if (!user || !address) {
                return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
            }
            dispatch({ type: 'LOADING' });

            const userID = user._id;
            user = JSON.parse(JSON.stringify(user));
            delete address._id;
            if (!user.profile.business) {
                user.profile.addresses.push(address);
                await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SET_PROFILE(userID), { profile: user.profile });
            }
            else {
                const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_GET(user.profile.business._id));
                const businessUser = res.user;
                delete address._id;
                businessUser.profile.office.addresses.push(address);
                await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_SET_PROFILE(businessUser._id), { profile: businessUser.profile });
            }
            const res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ACCOUNT_GET(userID));
            user = res.user;
            dispatch({ type: 'LOADED' });

            callback && callback(user.profile.business?.profile.office.addresses[user.profile.business?.profile.office.addresses.length - 1]
                || user.profile.addresses[user.profile.addresses.length - 1]);
        }
        catch (err) {
            console.log(err);
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const saveOrder = useCallback(async ({ order, callback }) => {

        let error = UtilityAPI.validateEntity({
            order,
            conditions: {
                user: ({ userID }) => userID,
                items: ({ items }) => items?.length,
            }
        });

        if (error) {
            console.log(error);
            return dispatch({ type: 'SET_STATE_DATA', payload: { requireCheck: true } });
        }

        order = { ...order };
        delete order.users;
        delete order.user;

        dispatch({ type: 'LOADING' });

        try {
            const res = await asyncServiceCall(order._id
                ? Commands.API_URL_BITBITE_BO_ORDER_EDIT(order._id)
                : Commands.API_URL_BITBITE_BO_ORDER_NEW, { order });
            dispatch({ type: 'LOADED' });
            dispatch({ type: 'SET_STATE_DATA', payload: { order: undefined, newProfile: undefined, updateOrderData: undefined } });
            !callback && dispatch({ type: 'SUCCESS', payload: Object.assign({}, { text: 'اطلاعات با موفقیت ثبت شد.', type: 'success' }) });
            callback && callback(res.order);
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);

    //--------------------------------------------------------
    const getOrder = useCallback(async ({ orderID }) => {
        if (!orderID) {
            console.log('orderID is null');
            return;
        }
        try {
            dispatch({ type: 'LOADING' });
            let res = await asyncServiceCall(Commands.API_URL_BITBITE_BO_ORDER_GET(orderID));
            dispatch({ type: 'LOADED' });

            dispatch({ type: 'SET_STATE_DATA', payload: { order: res.order } });
        }
        catch (err) {
            dispatch({ type: 'LOAD_FAILED', payload: Object.assign({}, { text: err.response?.data?.message || (err + ""), type: 'danger' }) });
        }
    }, [dispatch, asyncServiceCall]);


    return (
        <OrderContext.Provider
            value={{
                state,
                ...state,
                dispatch,
                getOrdersCount,
                getOrders,
                confirmOrder,
                orderPacked,
                orderDelivered,
                getNextOrder,
                orderRequiresCooking,
                orderReadyToPack,
                orderIsNotReady,
                prepareOrder,
                saveOrder,
                createNewAccount,
                saveNewAddress,
                getOrder,
            }}
        >
            {children}
        </OrderContext.Provider>
    )
}

export default OrderState;
